<script setup lang="ts">
import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic'

type ProductVariantSelectorsProps = {
  productStoreLocation: ProductStoreLocation
}

const props = defineProps<ProductVariantSelectorsProps>()
const emit = defineEmits<{ 'add-to-bag': [] }>()
const productStore = useProductStore(props.productStoreLocation)
const { pdp: pdpPreferences } = usePreferencesStore()
const showSizeSelector = ref(false)
const showSizeGuide = ref(false)
const openedFromAddToBag = ref(false)

const colorAttribute = computed(() =>
  productStore.selectedProduct?.variationAttributes?.find(
    // it is not possible to have more then one of the below color groups in a single product
    (value) => value.id === 'PIMColourMetal' || value.id === 'PIMColourGold' || value.id === 'PIMColourPWBRings',
  ),
)

const selectedAttributeId = ref('')
const selectorAttributes = computed(() => {
  return productStore.selectedProduct?.variationAttributes?.filter(
    (value) => value.id !== 'PIMColourMetal' && value.id !== 'PIMColourGold' && value.id !== 'PIMColourPWBRings',
  )
})
const attributeOptions = computed(() => {
  return (
    selectorAttributes.value?.flatMap(
      (attribute) =>
        attribute.values?.map((value) => ({
          label: value.name ?? value.value,
          value: value.value,
        })) ?? [],
    ) ?? []
  )
})

const selectedAttribute = computed(() => {
  return attributeOptions.value.find((attributeOption) => attributeOption.value === selectedAttributeId.value)
})

const ringSizes = computed(() => {
  const retailCategory = productStore.selectedProduct?.c_mHJRetailCategory
  if (!retailCategory?.includes('Rings')) {
    return []
  }
  return (
    productStore.selectedProduct?.c_mhj_processedSizeOptions?.map((option) => {
      return {
        label: option.size,
        value: option.size,
        available: option.inStock || !!option.isSpecialOrder || !!option.resizeAvailable,
      }
    }) ?? []
  )
})

const sizeGuide = computed(() => {
  if (!pdpPreferences?.sizeGuide) return []

  const retailCategory = productStore.selectedProduct?.c_mHJRetailCategory
  const isBanglesBracelets = retailCategory?.includes('Bangles') || retailCategory?.includes('Bracelets')
  const isNecklacesChains = retailCategory?.includes('Necklaces') || retailCategory?.includes('Chains')
  // ProductSizeGuide renders richTextPanel fields, so unwrap the matched Contentful entry
  const getSizeGuideByCategory = (category: string) => {
    const entry = pdpPreferences.sizeGuide.find(
      (item: any) => item.fields?.supportingData?.home?.sizeGuideCategory?.value === category,
    )
    return entry ? [entry.fields] : []
  }

  if (ringSizes.value.length > 0) {
    const rings = getSizeGuideByCategory('rings')
    return rings.length > 0 ? rings : getSizeGuideByCategory('')
  }

  if (isBanglesBracelets) {
    return getSizeGuideByCategory('banglesAndBracelets')
  }

  if (isNecklacesChains) {
    return getSizeGuideByCategory('necklacesAndChains')
  }

  return []
})

const isRingProduct = computed(() => ringSizes.value.length > 0)

const showAttributeSelector = computed(
  () => (attribute: ShopperProductsTypes.VariationAttribute) =>
    !attribute.id.includes('PIMColour') &&
    attribute.values &&
    ((attribute.values.length > 1 && props.productStoreLocation === 'pdp') || attribute.values.length === 1),
)

const isPdpOrSingleRingQuickview = computed(
  () =>
    props.productStoreLocation === 'pdp' ||
    (props.productStoreLocation === 'quickview' && ringSizes.value.length === 1),
)

const onRingSizeSelected = () => {
  productStore.setRingSizeError('')
  productStore.rememberSelectedRingSize()
  if (productStore.selectedProduct) {
    trackProductAttributeChange(productStore.selectedProduct, {
      name: 'size',
      value: productStore.selectedRingSize || '',
    })
  }
  if (openedFromAddToBag.value) {
    openedFromAddToBag.value = false
    emit('add-to-bag')
  }
}

const onVariantSelected = async (attribute: ShopperProductsTypes.VariationAttribute) => {
  const newVariant = productStore.selectedProduct?.variants?.find((variant) => {
    return variant.variationValues?.[attribute.id] === selectedAttribute.value?.value
  })

  if (!newVariant) return

  await productStore.getNewVariant(newVariant.productId)

  if (productStore.selectedProduct && selectedAttribute.value?.label && selectedAttribute.value?.value) {
    trackProductAttributeChange(productStore.selectedProduct, {
      name: selectedAttribute.value.label,
      value: selectedAttribute.value.value,
    })
  }
}

const openSizeSelector = (addToBag = false) => {
  openedFromAddToBag.value = addToBag
  showSizeSelector.value = true
}

const onSizeSelectorClose = () => {
  showSizeSelector.value = false
  openedFromAddToBag.value = false
}

defineExpose({
  openSizeSelector,
})
</script>

<template>
  <div class="product-variant-selectors">
    <!-- for colour level attributes -->
    <ProductColorSelector
      v-if="colorAttribute"
      :attribute="colorAttribute"
      class="product-variant-selectors__product-color-selector"
      :productStoreLocation="productStoreLocation"
    />

    <!-- for attributes that are not colour and when size is part of a variation group  -->
    <div
      v-if="!isRingProduct"
      v-for="attribute in selectorAttributes"
      :key="attribute.id"
      class="product-variant-selectors__metal-color-container"
    >
      <template v-if="showAttributeSelector(attribute)">
        <section class="product-variant-selectors__wrapper">
          <div class="product-variant-selectors__size">
            <span>
              {{ attribute.name }}:
              <template v-if="attribute.values?.length === 1">
                {{ attribute.values[0]?.name ?? attribute.values[0]?.value }}
              </template>
            </span>
            <Button v-if="productStoreLocation === 'pdp'" @click="showSizeGuide = true">
              {{ $t('product.variant_selector.ring_size_text') }}
            </Button>
          </div>

          <Button
            v-if="attribute.values && attribute.values.length > 1"
            variant="selector"
            data-testid="pdp-size-selector-trigger"
            @click="showSizeSelector = true"
          >
            {{ selectedAttribute?.label || `${$t('product.selector.option_select_text')} ${attribute.name}` }}
          </Button>
        </section>
      </template>

      <!-- Fallback to ProductSelector for other cases -->
      <ProductSelector v-else :variationAttribute="attribute" :productStoreLocation="productStoreLocation" />
    </div>

    <ProductAvailability
      v-if="!isRingProduct && productStoreLocation !== 'quickview'"
      :product-store-location="productStoreLocation"
      @open-size-selector="openSizeSelector"
    />

    <!-- Ring sizes for PDP and single-ring quickview -->
    <section v-else-if="isPdpOrSingleRingQuickview" class="product-variant-selectors__wrapper">
      <div class="product-variant-selectors__size">
        <span>{{
          ringSizes.length > 1
            ? `${$t('product.variant_selector.selector_title')}:`
            : $t('product.selector.selected_size', { size: ringSizes[0]?.label })
        }}</span>
        <Button v-if="productStoreLocation === 'pdp'" @click="showSizeGuide = true">
          {{ $t('product.variant_selector.ring_size_text') }}
        </Button>
      </div>

      <!-- Ring size selector button (only for multiple sizes) -->
      <Button
        v-if="ringSizes.length > 1"
        variant="selector"
        data-testid="pdp-size-selector-trigger"
        :class="{ 'product-variant-selectors__button--error': productStore.ringSizeError }"
        @click="showSizeSelector = true"
      >
        {{ productStore.selectedRingSize || $t('product.selector.select_size_text') }}
      </Button>
      <ProductAvailability
        v-if="productStoreLocation !== 'quickview'"
        :product-store-location="productStoreLocation"
        @open-size-selector="openSizeSelector"
      />
      <span v-if="productStore.ringSizeError" class="product-variant-selectors__error">{{
        productStore.ringSizeError
      }}</span>
    </section>

    <!-- Ring sizes dropdown for multi-ring quickview -->
    <Select
      v-else-if="isRingProduct"
      v-model="productStore.selectedRingSize"
      :name="$t('product.variant_selector.selector_title')"
      :label="$t('product.variant_selector.selector_title')"
      :options="ringSizes"
      select-test-id="product-variant-selectors__ring-size"
      :default="$t('product.selector.please_select_size_text')"
      :error="productStore.ringSizeError"
      :attempt="productStore.ringSizeErrorAttempt"
      required
      @update:modelValue="onRingSizeSelected"
    />

    <!-- Size Selector Modal -->
    <Modal variant="slide" :show="showSizeSelector" @close="onSizeSelectorClose">
      <ProductSizeSelector
        v-if="isRingProduct"
        v-model="productStore.selectedRingSize"
        :sizes="ringSizes"
        :productStoreLocation="productStoreLocation"
        :size-guide="sizeGuide"
        @close="showSizeSelector = false"
        @update:modelValue="onRingSizeSelected"
      />
      <ProductSizeSelector
        v-else
        v-model="selectedAttributeId"
        :sizes="attributeOptions"
        :productStoreLocation="productStoreLocation"
        :size-guide="sizeGuide"
        @close="showSizeSelector = false"
        @update:modelValue="onVariantSelected(selectorAttributes?.[0]!)"
      />
    </Modal>

    <!-- Size Guide Modal -->
    <Modal variant="slide" :show="showSizeGuide" @close="showSizeGuide = false">
      <h1 class="product-variant-selectors__size-guide-title">{{ $t('product.size_guide.heading') }}</h1>
      <ProductSizeGuide v-if="sizeGuide.length" :size-guide="sizeGuide" :location="productStoreLocation" />
    </Modal>
  </div>
</template>

<style lang="postcss">
.product-variant-selectors {
  display: flex;
  flex-direction: column;
  gap: var(--space-50);

  .product-variant-selectors__product-color-selector {
    padding-bottom: var(--space-50);
    border-bottom: 1px solid var(--border-neutral-20);
  }

  .product-variant-selectors__wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--space-30);
    padding-bottom: var(--space-50);
    border-bottom: 1px solid var(--border-neutral-20);

    .product-variant-selectors__size {
      display: flex;
      justify-content: space-between;

      font-size: var(--text-small);

      span {
        text-transform: uppercase;
        line-height: 1.25;
      }

      button {
        text-transform: none;
      }
    }
    .product-variant-selectors__button--error {
      border-color: var(--status-error);
    }

    .product-availability {
      margin-top: calc(0px - var(--space-30));
    }
  }

  .product-variant-selectors__size-guide-title {
    font-size: 1.75rem;
    text-align: center;
  }

  .modal .modal__header {
    position: unset;
  }
  .product-variant-selectors__error {
    color: var(--status-error);
  }
}
</style>
